import {
  clearingPaidText,
  shownInvoiceMoney,
  type ShownMoneyInput,
} from '@/lib/invoices/shown-invoice-money';
import type { Invoice } from '@/types/invoice';
import {
  settlingHasSeveralInFlight,
  settlingInFlightTotal,
  settlingPaymentShortfall,
} from './invoice-next-step';

/**
 * The one-line note an invoice list shows for money in flight, or null when
 * nothing is. The detail view has the full banner; a list needs just enough
 * that an invoice with a clearing payment doesn't read as plainly unpaid.
 *
 * A bank debit is on its normal multi-day path. A pending card charge is an
 * outcome still being verified, so the two don't share wording.
 */
export function invoiceClearingLabel(
  settlingPayment: Invoice['settlingPayment'],
  formatMoney: (amount: number) => string,
): string | null {
  if (!settlingPayment) return null;
  const amount = formatMoney(settlingInFlightTotal(settlingPayment));
  // `rail` describes only the oldest payment, while the amount is the total of
  // every one in flight — a $300 bank debit and a $200 card charge must not read
  // as "$500 clearing". Several in flight get neutral wording, as the banner does.
  if (settlingHasSeveralInFlight(settlingPayment)) return `${amount} in transit`;
  return settlingPayment.rail === 'BANK' ? `${amount} pending ACH` : `${amount} being verified`;
}

/**
 * The clearing text a staff invoice row shows: "$250.00 paid, clearing" when the
 * server returned clearing figures, else the settling-payment label ("$120.00
 * pending ACH") for an older API or a card in flight. Null when neither applies.
 * Every row surface (list row, table cell, job row) calls this, so the fallback
 * is written once.
 */
export function invoiceRowClearingText(
  invoice: ShownMoneyInput & Pick<Invoice, 'settlingPayment'>,
  formatMoney: (amount: number) => string,
): string | null {
  return (
    clearingPaidText(invoice, formatMoney) ??
    invoiceClearingLabel(invoice.settlingPayment, formatMoney)
  );
}

/**
 * A list row's money line while bank money clears: "Balance $0.00 · $250.00
 * paid, clearing". Built on `invoiceRowClearingText`, so the fallback (a
 * pending card charge, or an older API without clearing figures) is still the
 * settling-payment label, unprefixed: a balance is only netted when the server
 * sent the figures that net it. A balance the viewer cannot read is left off
 * rather than printed as $0.00. Null when nothing is in flight.
 */
export function invoiceRowClearingLine(
  invoice: ShownMoneyInput & Pick<Invoice, 'settlingPayment'>,
  formatMoney: (amount: number) => string,
): string | null {
  const text = invoiceRowClearingText(invoice, formatMoney);
  if (!text || !clearingPaidText(invoice, formatMoney)) return text;
  const { balance } = shownInvoiceMoney(invoice);
  return balance === null ? text : `Balance ${formatMoney(balance)} · ${text}`;
}

/**
 * Why a STAFF collection control on this invoice is disabled, or null when it
 * is not.
 *
 * Money already in flight covers the balance. Offering "Record payment" beside
 * it invites the second collection the customer is not expecting — the same
 * reason the deposit CTA goes quiet on a job whose deposit is clearing. The
 * control is disabled rather than removed: an operator who cannot see why the
 * button vanished assumes the page is broken, and this is the one place that
 * says an ACH is on its way.
 *
 * Only when the money in flight COVERS the balance. A part-paid invoice with a
 * shortfall still has real money to take, and locking that would leave staff
 * no way to collect the rest for the business days the debit takes to clear.
 *
 * Without `settlingPayment` (withheld from an actor without `payments:view`
 * or under a financial mask) the clearing figures answer instead, where the
 * actor is shown them: a role that reads the balance reads the same Clearing
 * as everyone else, and a Record payment the server would refuse is no use to
 * it. Under a mask that withholds the figures too, an absent field is not
 * evidence that nothing is clearing: a technician taking a cheque at the door
 * keeps the control they have always had, rather than a dead button with no
 * reason attached.
 *
 * Null too when the balance itself cannot be read. `settlingPaymentShortfall`
 * answers "covered" for an unreadable balance — right for dunning copy, which
 * errs towards not chasing, and exactly backwards here, where the same answer
 * TAKES A CONTROL AWAY. Today the masks that delete `balanceDue` are a subset
 * of those that null `settlingPayment`, so the guard above already catches it;
 * this one does not depend on that staying true, because one edit to either
 * mask list would otherwise disable Record payment for a field technician —
 * the regression `hasCollectableBalance` exists to prevent.
 *
 * Payer surfaces are deliberately NOT callers. The public invoice, the public
 * quote and the portal each explain the wait in a banner and let the customer
 * decide; this is about staff collecting a second time by accident.
 */
export function invoiceCollectionBlockedReason(
  invoice: Pick<Invoice, 'settlingPayment' | 'balanceDue'> &
    Partial<
      Pick<ShownMoneyInput, 'total' | 'amountPaid' | 'clearingBankTotal' | 'clearingBankCash'>
    >,
  formatMoney: (amount: number) => string,
): string | null {
  if (typeof invoice.balanceDue !== 'number' || !Number.isFinite(invoice.balanceDue)) return null;
  const settlingPayment = invoice.settlingPayment;
  if (!settlingPayment) {
    const shown = shownInvoiceMoney({
      total: invoice.total ?? 0,
      amountPaid: invoice.amountPaid ?? 0,
      balanceDue: invoice.balanceDue,
      clearingBankTotal: invoice.clearingBankTotal,
      clearingBankCash: invoice.clearingBankCash,
    });
    return shown.covers ? `${formatMoney(shown.clearingNote)} pending ACH` : null;
  }
  if (settlingPaymentShortfall(invoice.balanceDue, settlingPayment) !== null) return null;
  return invoiceClearingLabel(settlingPayment, formatMoney);
}
